import { DialogOverlay, DialogContent } from '@reach/dialog';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { variant } from 'styled-system';
import css from '@styled-system/css';
import { Box } from '../Layout';

const overlayEnter = keyframes`
  0% {
    background-color: hsla(0, 0%, 0%, 0);
  }
  100% {
    background-color: hsla(0, 0%, 0%, 0.4);
  }
`;

const overlayExit = keyframes`
  0% {
    background-color: hsla(0, 0%, 0%, 0.4);
  }
  100% {
    background-color: hsla(0, 0%, 0%, 0);
  }
`;

const contentEnter = keyframes`
  0% {
    transform: translateY(-50px);
    opacity: 0;
  }
  30% {
    transform: translateY(-50px);
    opacity: 0;
  }
  100% {
    transform: none;
    opacity: 1;
  }
`;

const contentExit = keyframes`
  0% {
    transform: none;
    opacity: 1;
  }
  100% {
    transform: translateY(-50px);
    opacity: 0;
  }
`;

const Overlay = styled(DialogOverlay)(
  {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    backgroundColor: 'hsla(0, 0%, 0%, 0)',
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'auto',
  },
  (props) => ({
    animation:
      props.state === 'entering' || props.state === 'entered'
        ? `${overlayEnter} 300ms ease-out forwards`
        : props.state === 'exiting' || props.state === 'exited'
        ? `${overlayExit} 200ms ease-in forwards`
        : undefined,
  }),
);

const Content = styled(DialogContent)(
  css({
    position: 'relative',
    border: 'none',
    maxWidth: '100%',
    backgroundColor: 'background',
  }),
  variant({
    prop: 'size',
    scale: 'components.Modal.sizes',
    variants: {
      small: {
        width: '440px',
      },
      medium: {
        width: '660px',
      },
      large: {
        width: '990px',
      },
      full: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
    },
  }),
  ({ state, size, theme }) => ({
    margin: size === 'full' ? 'none' : 'auto',
    borderRadius: size === 'full' ? 'none' : theme.radii.modal,
    animation:
      state === 'entering' || state === 'entered'
        ? `${contentEnter} 500ms ease-out forwards`
        : state === 'exiting' || state === 'exited'
        ? `${contentExit} 200ms ease-in forwards`
        : undefined,
  }),
);

const Line = styled('hr')(({ theme }) =>
  css({
    width: `calc(100% + ${theme.space.medium * 2}px)`,
    borderTopStyle: 'solid',
    borderTopWidth: 'divider',
    borderTopColor: 'divider',
    marginLeft: `-${theme.space.medium}px`,
  })(theme),
);

const Body = styled('div')((props) =>
  props.padding === 'none'
    ? {
        width: `calc(100% + ${props.theme.space.medium * 2}px)`,
        marginLeft: `-${props.theme.space.medium}px`,
        marginBottom: `-${props.theme.space.medium}px`,
        marginTop: 0,
      }
    : {
        padding: 0,
      },
);

const Footer = styled(Box)(
  css({
    width: '100%',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: 'borderAccent',
    backgroundColor: 'backgroundAccent',
    borderBottomLeftRadius: 'modal',
    borderBottomRightRadius: 'modal',
  }),
  (props) =>
    props.footerMargin === 'left' && {
      '> *:first-of-type:last-child': {
        marginLeft: 'auto',
      },
    },
);

export { Overlay, Content, Body, Footer, Line };
